import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Card, Table, TableBody, TableCell, TableHead, TableRow,
  Typography, Button, TextField, IconButton, TablePagination,
  InputAdornment, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, Grid, Switch, FormControlLabel, Tooltip
} from '@mui/material';
import { Add, Search, Edit, Delete } from '@mui/icons-material';
import { API } from '../context/AuthContext';
import { fmt } from '../utils';
import { useSnackbar } from 'notistack';

const DEFAULT = { name: '', code: '', description: '', category: '', unit: 'PZA', price: 0, cost: 0, taxRate: 16, trackStock: false, stock: 0 };

function ProductDialog({ open, product, onClose, onSave }) {
  const { enqueueSnackbar } = useSnackbar();
  const [form, setForm] = useState(DEFAULT);
  const [loading, setLoading] = useState(false);
  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));

  useEffect(() => { setForm(product ? { ...DEFAULT, ...product } : DEFAULT); }, [product, open]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (product?._id) await API.put(`/products/${product._id}`, form);
      else await API.post('/products', form);
      enqueueSnackbar(product ? 'Producto actualizado' : 'Producto creado', { variant: 'success' });
      onSave();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.error || 'Error', { variant: 'error' });
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '20px' } }}>
      <DialogTitle fontWeight={700}>{product ? 'Editar artículo' : 'Nuevo artículo'}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={8}>
            <TextField fullWidth label="Nombre *" value={form.name} onChange={e => set('name', e.target.value)} required />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Código" value={form.code} onChange={e => set('code', e.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="Descripción" value={form.description} onChange={e => set('description', e.target.value)} multiline rows={2} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Categoría" value={form.category} onChange={e => set('category', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Unidad" value={form.unit} onChange={e => set('unit', e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Precio de venta *" type="number" value={form.price} onChange={e => set('price', parseFloat(e.target.value)||0)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Costo" type="number" value={form.cost} onChange={e => set('cost', parseFloat(e.target.value)||0)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="IVA %" type="number" value={form.taxRate} onChange={e => set('taxRate', parseFloat(e.target.value)||0)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel control={<Switch checked={form.trackStock} onChange={e => set('trackStock', e.target.checked)} />} label="Controlar inventario" />
          </Grid>
          {form.trackStock && (
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Stock actual" type="number" value={form.stock} onChange={e => set('stock', parseInt(e.target.value)||0)} />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2.5, gap: 1 }}>
        <Button onClick={onClose} color="inherit">Cancelar</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading || !form.name}>
          {loading ? <CircularProgress size={20} /> : (product ? 'Guardar' : 'Crear artículo')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function ProductsPage() {
  const { enqueueSnackbar } = useSnackbar();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [dialog, setDialog] = useState({ open: false, product: null });

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    const { data } = await API.get('/products', { params: { search, page: page + 1, limit: 20 } });
    setProducts(data.products); setTotal(data.total);
    setLoading(false);
  }, [search, page]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const deleteProduct = async (id) => {
    if (!window.confirm('¿Eliminar este artículo?')) return;
    await API.delete(`/products/${id}`);
    enqueueSnackbar('Artículo eliminado', { variant: 'info' });
    fetchProducts();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={800} color="#1E1B2E">Artículos / Productos</Typography>
          <Typography variant="body2" color="text.secondary">{total} artículos registrados</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => setDialog({ open: true, product: null })}>
          Nuevo artículo
        </Button>
      </Box>

      <Card>
        <Box sx={{ p: 2 }}>
          <TextField placeholder="Buscar por nombre, código…" value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
            sx={{ width: 300 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ color: 'text.secondary' }} /></InputAdornment> }} />
        </Box>

        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Código</TableCell>
              <TableCell>Nombre</TableCell>
              <TableCell>Categoría</TableCell>
              <TableCell>Unidad</TableCell>
              <TableCell align="right">Precio</TableCell>
              <TableCell align="right">Costo</TableCell>
              <TableCell align="right">IVA</TableCell>
              <TableCell align="right">Stock</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={9} align="center" sx={{ py: 6 }}><CircularProgress size={32} /></TableCell></TableRow>
            ) : products.length === 0 ? (
              <TableRow><TableCell colSpan={9} align="center" sx={{ py: 6, color: 'text.secondary' }}>Sin artículos. Agrega el primero.</TableCell></TableRow>
            ) : products.map(p => (
              <TableRow key={p._id} hover>
                <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{p.code || '-'}</Typography></TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>{p.name}</Typography>
                  {p.description && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 280 }}>{p.description}</Typography>}
                </TableCell>
                <TableCell><Typography variant="body2">{p.category || '-'}</Typography></TableCell>
                <TableCell><Typography variant="body2">{p.unit}</Typography></TableCell>
                <TableCell align="right"><Typography variant="body2" fontWeight={700}>{fmt.currency(p.price)}</Typography></TableCell>
                <TableCell align="right"><Typography variant="body2">{fmt.currency(p.cost)}</Typography></TableCell>
                <TableCell align="right"><Typography variant="body2">{p.taxRate}%</Typography></TableCell>
                <TableCell align="right">
                  {p.trackStock ? <Typography variant="body2" fontWeight={600} color={p.stock > 0 ? 'success.main' : 'error.main'}>{p.stock}</Typography> : <Typography variant="caption" color="text.secondary">N/A</Typography>}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Editar">
                    <IconButton size="small" onClick={() => setDialog({ open: true, product: p })}><Edit fontSize="small" /></IconButton>
                  </Tooltip>
                  <Tooltip title="Eliminar">
                    <IconButton size="small" color="error" onClick={() => deleteProduct(p._id)}><Delete fontSize="small" /></IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination component="div" count={total} page={page} rowsPerPage={20}
          onPageChange={(_, p) => setPage(p)} rowsPerPageOptions={[20]}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`} />
      </Card>

      <ProductDialog open={dialog.open} product={dialog.product}
        onClose={() => setDialog({ open: false, product: null })}
        onSave={() => { setDialog({ open: false, product: null }); fetchProducts(); }} />
    </Box>
  );
}
